import type { EditOp } from "../contracts/edit-graph.js";
import type { CaptionTokenId, Milli } from "../contracts/ids.js";

/** Authored caption look — not a honesty/dishonesty label. */
export type CaptionAuthoredStyle = "clean" | "documentary" | "kinetic" | "urgent";

/** Deterministic reveal animation for caption tokens. */
export type CaptionRevealMode = "static" | "word-by-word" | "pop";

export const DEFAULT_CAPTION_STYLE: CaptionAuthoredStyle = "documentary";
export const DEFAULT_CAPTION_REVEAL: CaptionRevealMode = "static";
export const DEFAULT_CAPTION_SCALE_MILLI = 1000;
/** Millipoints of frame (0–1000); center-x / lower-third-y defaults. */
export const DEFAULT_CAPTION_ANCHOR_X_MILLI = 500;
export const DEFAULT_CAPTION_ANCHOR_Y_MILLI = 820;

export const CAPTION_STYLES = new Set<CaptionAuthoredStyle>([
  "clean",
  "documentary",
  "kinetic",
  "urgent",
]);

export const CAPTION_REVEAL_MODES = new Set<CaptionRevealMode>([
  "static",
  "word-by-word",
  "pop",
]);

export interface CaptionPresentation {
  style: CaptionAuthoredStyle;
  revealMode: CaptionRevealMode;
  scaleMilli: Milli;
  anchorXMilli: Milli;
  anchorYMilli: Milli;
  emphasizedTokenIds: CaptionTokenId[];
}

export type TextEditOp = Extract<EditOp, { kind: "text" }>;

/** Fill optional presentation fields with source-compatible defaults. */
export function normalizeCaptionPresentation(op: TextEditOp): CaptionPresentation {
  return {
    style: op.style ?? DEFAULT_CAPTION_STYLE,
    revealMode: op.revealMode ?? DEFAULT_CAPTION_REVEAL,
    scaleMilli: op.scaleMilli ?? DEFAULT_CAPTION_SCALE_MILLI,
    anchorXMilli: op.anchorXMilli ?? DEFAULT_CAPTION_ANCHOR_X_MILLI,
    anchorYMilli: op.anchorYMilli ?? DEFAULT_CAPTION_ANCHOR_Y_MILLI,
    emphasizedTokenIds: op.emphasizedTokenIds ? [...op.emphasizedTokenIds] : [],
  };
}

export function isDefaultCaptionPresentation(p: CaptionPresentation): boolean {
  return (
    p.style === DEFAULT_CAPTION_STYLE &&
    p.revealMode === DEFAULT_CAPTION_REVEAL &&
    p.scaleMilli === DEFAULT_CAPTION_SCALE_MILLI &&
    p.anchorXMilli === DEFAULT_CAPTION_ANCHOR_X_MILLI &&
    p.anchorYMilli === DEFAULT_CAPTION_ANCHOR_Y_MILLI &&
    p.emphasizedTokenIds.length === 0
  );
}

/**
 * Bounded production / intensity cosmetics from authored caption presentation.
 * Defaults yield zero so legacy graphs keep prior score behavior.
 * No style maps to honesty/dishonesty or claimDrift.
 */
export function captionPresentationScoreBoost(op: TextEditOp): {
  intensityMilli: Milli;
  productionMilli: Milli;
} {
  const p = normalizeCaptionPresentation(op);
  if (isDefaultCaptionPresentation(p)) {
    return { intensityMilli: 0, productionMilli: 0 };
  }

  let intensityMilli = 0;
  let productionMilli = 0;

  switch (p.style) {
    case "clean":
      productionMilli += 20;
      break;
    case "documentary":
      break;
    case "kinetic":
      intensityMilli += 30;
      productionMilli += 15;
      break;
    case "urgent":
      intensityMilli += 50;
      productionMilli += 10;
      break;
  }

  switch (p.revealMode) {
    case "static":
      break;
    case "word-by-word":
      productionMilli += 25;
      break;
    case "pop":
      intensityMilli += 20;
      productionMilli += 10;
      break;
  }

  const scaleDelta = Math.abs(p.scaleMilli - DEFAULT_CAPTION_SCALE_MILLI);
  if (scaleDelta > 0) {
    productionMilli += Math.min(40, (scaleDelta / 25) | 0);
  }

  const anchorDelta =
    Math.abs(p.anchorXMilli - DEFAULT_CAPTION_ANCHOR_X_MILLI) +
    Math.abs(p.anchorYMilli - DEFAULT_CAPTION_ANCHOR_Y_MILLI);
  if (anchorDelta > 0) {
    productionMilli += Math.min(30, (anchorDelta / 50) | 0);
  }

  if (p.emphasizedTokenIds.length > 0) {
    intensityMilli += Math.min(45, p.emphasizedTokenIds.length * 15);
  }

  return {
    intensityMilli: Math.min(120, intensityMilli),
    productionMilli: Math.min(100, productionMilli),
  };
}

/**
 * Deterministic visible token count for reveal modes at a source tick.
 * progress ∈ [0,1] within [startTick, endTick).
 */
export function captionRevealVisibleCount(
  tokenCount: number,
  revealMode: CaptionRevealMode,
  progress01: number,
): number {
  if (tokenCount <= 0) return 0;
  const p = Math.max(0, Math.min(1, progress01));
  switch (revealMode) {
    case "static":
      return tokenCount;
    case "word-by-word": {
      // Reveal across first 70% of the caption window.
      const phase = Math.min(1, p / 0.7);
      return Math.max(1, Math.min(tokenCount, Math.ceil(phase * tokenCount)));
    }
    case "pop":
      return p >= 0.12 ? tokenCount : 0;
  }
}

export function captionProgress01(
  sourceTick: number,
  startTick: number,
  endTick: number,
): number {
  const span = endTick - startTick;
  if (span <= 0) return 0;
  return Math.max(0, Math.min(1, (sourceTick - startTick) / span));
}
